import React, {Fragment, useContext, useEffect, useState, useCallback} from 'react';
import {Link} from "react-router";
import {Checkbox, Tag} from "@blueprintjs/core";

import {CreateOrganisation} from "./create";
import {SearchableDataGrid} from "../../../components/tables/data-grid";
import {organisations} from "../../../services/api";
import {errorToast, successToast} from "../../../utils/toaster";
import {DataPageContext} from "../context";
import {timeAgo} from "../../../utils/time";


// -------------------------------------------------
// ----------------------------------- columns model
const OrganisationModelColumns = [
    {
        key: 'name',
        name: 'Name',
        resizable: true,
        sortable: true,
        filterable: true,
        showDefaultFilter: true,
        formatter: (params) =>
            <Link to={'/orgs/' + params.row.id}>
                {params.row.name}
            </Link>
    },
    {
        key: 'locations',
        name: 'Locations',
        width: 90,
        formatter: (params) =>
            <Link to={'/locs?org=' + params.row.id}>
                <Tag round interactive>
                    Locations
                </Tag>
            </Link>
    },
    {
        key: 'devices',
        name: 'Devices',
        width: 90,
        formatter: (params) =>
            <Link to={'/devs?org=' + params.row.id}>
                <Tag round interactive>
                    Devices
                </Tag>
            </Link>
    },
    {
        key: 'cameras',
        name: 'Cameras',
        width: 90,
        formatter: (params) =>
            <Link to={'/recs?org=' + params.row.id}>
                <Tag round interactive>
                    Cameras
                </Tag>
            </Link>
    },
    {
        key: 'users',
        name: 'Users',
        width: 90,
        formatter: (params) =>
            <Link to={'/orgs/' + params.row.id + '/users'}>
                <Tag round interactive>
                    Users
                </Tag>
            </Link>
    },
    {
        key: 'options.showSettingUpLocations',
        name: 'Show Setting Up Locations',
        resizable: true,
        sortable: true,
        formatter: (params) => <Checkbox checked={!!params.row?.options?.showSettingUpLocations} />
    },
    {
        key: 'options.startOfWeek',
        name: 'Start of Week',
        resizable: true,
        sortable: true,
        filterable: true,
        formatter: (params) => params.row?.options?.startOfWeek || 'default'
    },
    {
        key: 'createdAt',
        name: 'Created',
        width: 100,
        resizable: true,
        sortable: true,
        formatter: (params) =>
            <span title={params.row.createdAt}>{timeAgo(params.row.createdAt)}</span>,
    },
    {
        key: 'updatedAt',
        name: 'Updated',
        width: 100,
        resizable: true,
        sortable: true,
        formatter: (params) =>
            <span title={params.row.updatedAt}>{timeAgo(params.row.updatedAt)}</span>,
    },
];


const ManyOrgs = () => {
    const {setLoading, setNav, setCount} = useContext(DataPageContext);
    const [data, setData] = useState(null);
    const [creating, setCreating] = useState(false);

    const toggleCreating = useCallback(() => {
        setCreating(prev => !prev);
    }, []);

    useEffect(() => {
        setNav({right: [
            {name: "Create", onClick: toggleCreating, icon: "add"}
        ]});
    }, [toggleCreating]);

    useEffect(() => {
        console.log("loading orgs");
        setLoading({status: true, location: "Loading organisations..."});

        organisations.list().then(response => {
            console.log("Loaded " + response.length + " orgs");
            setData(response);
            setLoading({status: false, location: "All organisations"});
        }).catch(error => {
            console.error(error);
            setLoading({status: false, location: "Failed to load organisations..."});
            setData(error);
        });
    }, []);

    return (
        <Fragment>
            <CreateOrganisation
                isOpen={creating}
                onClose={toggleCreating}
                onSave={(info) => {
                    const currentNavContext = "All organisations";
                    toggleCreating();
                    setLoading({status: true, location: `Creating organisation ${info.name}...`});
                    organisations.create(info).then((ret) => {
                        console.log("Created organisation: ", ret);
                        successToast({message: `Created organisation ${ret.name}`});
                        setData(prev => [...prev, ret]);
                    }).catch((err) => {
                        console.log(err);
                        errorToast({message: "Failed to create organisation: " + err.message, timeout: 5000});
                    }).finally(() => {
                        setLoading({status: false, location: currentNavContext});
                    });
                }}
            />
            <SearchableDataGrid
                columns={OrganisationModelColumns}
                data={data ?? []}
                defaultSort='name'
                onCount={(filtered, total) => setCount({filtered, total})}
            />
        </Fragment>
    );
}

export {ManyOrgs};
